import styled from '@/styles';

export const PageContent = styled('div', {
  maxWidth: 1100,
  width: '100%',
  margin: '32px 60px',
  alignSelf: 'center',
  padding: '0 24px',
  boxSizing: 'border-box',
  fontFamily: "'Manrope', sans-serif",
  display: 'flex',
  gap: 24,
  alignItems: 'flex-start',
});

export const LeftCol = styled('div', {
  flex: '3 1 0',
  minWidth: 0,
});

export const HeadingBlock = styled('div', {
  marginBottom: 16,
});

export const HeadingTitle = styled('p', {
  fontSize: 18,
  fontWeight: 800,
  color: '#1C1C1C',
  margin: '0 0 4px',
  fontFamily: "'Manrope', sans-serif",
});

export const HeadingSubtitle = styled('p', {
  fontSize: 14,
  color: '#5B5B5B',
  margin: 0,
  fontFamily: "'Manrope', sans-serif",
});

// ── Calendar card (overlay mode) ──────────────────────────────────────────

export const CalendarCard = styled('div', {
  background: '#fff',
  border: '1px solid #DADADA',
  borderRadius: 8,
  width: '100%',
  overflow: 'hidden',
});

export const MonthNavRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 16,
});

export const MonthNavBtn = styled('button', {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  display: 'flex',
  alignItems: 'center',
  color: '#1C1C1C',
});

export const MonthLabel = styled('span', {
  fontSize: 18,
  fontWeight: 700,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
});

export const DayHeadersRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
});

export const DayHeader = styled('div', {
  textAlign: 'center',
  fontSize: 12,
  fontWeight: 600,
  color: '#fff',
  background: '#44A735',
  padding: '9px 0',
  borderRight: '1px solid rgba(255,255,255,0.15)',
  fontFamily: "'Manrope', sans-serif",
  '&:last-child': { borderRight: 'none' },
});

export const DateGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  borderLeft: '1px solid #EAEAEA',
  borderTop: '1px solid #EAEAEA',
});

export const EmptyCell = styled('div', {
  minHeight: 90,
  borderRight: '1px solid #EAEAEA',
  borderBottom: '1px solid #EAEAEA',
  boxSizing: 'border-box',
});

export const DateCell = styled('div', {
  minHeight: 90,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  padding: '5px 5px 4px',
  cursor: 'pointer',
  boxSizing: 'border-box',
  borderRight: '1px solid #EAEAEA',
  borderBottom: '1px solid #EAEAEA',
  variants: {
    highlighted: {
      true: { background: '#E7F4E6' },
      false: {},
    },
  },
});

export const DateNumber = styled('span', {
  fontSize: 11,
  fontFamily: "'Manrope', sans-serif",
  alignSelf: 'flex-end',
  lineHeight: 1.2,
  variants: {
    state: {
      default: { fontWeight: 400, color: '#1C1C1C' },
      today: { fontWeight: 700, color: '#44A735' },
    },
  },
});

export const TimeChip = styled('div', {
  marginTop: 3,
  padding: '2px 5px',
  background: '#44A735',
  borderRadius: 3,
  fontSize: 10,
  fontWeight: 600,
  color: '#fff',
  fontFamily: "'Manrope', sans-serif",
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
  boxSizing: 'border-box',
});

export const PendingDot = styled('div', {
  marginTop: 3,
  width: 7,
  height: 7,
  borderRadius: '50%',
  background: '#44A735',
});

// ── Schedule panel (right column) ─────────────────────────────────────────

export const SchedulePanel = styled('div', {
  flex: '2 1 0',
  minWidth: 260,
  background: '#fff',
  border: '1px solid #DADADA',
  borderRadius: 12,
  overflow: 'hidden',
});

export const PanelHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '16px 20px',
  borderBottom: '1px solid #EAEAEA',
  variants: {
    tone: {
      default: { background: '#fff' },
      pricing: {
        background: '#EEFFEC',
        justifyContent: 'space-between',
      },
    },
  },
});

export const PanelHeaderTitleGroup = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minWidth: 0,
});

export const PanelEstBlock = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
});

export const PanelEstAmount = styled('span', {
  fontSize: 18,
  fontWeight: 800,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
});

export const PanelEstLabel = styled('span', {
  fontSize: 11,
  color: '#5B5B5B',
  fontFamily: "'Manrope', sans-serif",
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginTop: 2,
});

export const PanelRateSection = styled('div', {
  padding: '16px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  borderTop: '1px solid #EAEAEA',
});

export const PanelRateRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const PanelRateLabel = styled('span', {
  fontSize: 13,
  color: '#5B5B5B',
  fontFamily: "'Manrope', sans-serif",
});

export const PanelRateValue = styled('span', {
  fontSize: 14,
  fontWeight: 600,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
});

export const PanelTotalsSection = styled('div', {
  padding: '16px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  borderTop: '1px solid #EAEAEA',
});

export const PanelTotalsRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const PanelTotalsLabel = styled('span', {
  fontSize: 14,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
  variants: {
    weight: {
      regular: { fontWeight: 500 },
      bold: { fontWeight: 700 },
    },
  },
});

export const PanelTotalsValue = styled('span', {
  fontSize: 16,
  fontWeight: 700,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
});

// ── Pay rate / Bill rate input card (shown above the calendar for STATIC accounts) ──

export const RateCard = styled('div', {
  background: '#fff',
  border: '1px solid #DADADA',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
  fontFamily: "'Manrope', sans-serif",
});

export const RateCardTitle = styled('p', {
  fontSize: 15,
  fontWeight: 700,
  color: '#1C1C1C',
  margin: '0 0 4px',
  fontFamily: "'Manrope', sans-serif",
});

export const RateCardSubtitle = styled('p', {
  fontSize: 13,
  color: '#5B5B5B',
  margin: '0 0 12px',
  fontFamily: "'Manrope', sans-serif",
});

export const RateInputsRow = styled('div', {
  display: 'flex',
  gap: 12,
  flexWrap: 'wrap',
});

export const RateInputGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  flex: '1 1 160px',
  minWidth: 0,
});

export const RateInputLabel = styled('label', {
  fontSize: 12,
  fontWeight: 700,
  color: '#5B5B5B',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  fontFamily: "'Manrope', sans-serif",
});

export const RateInputShell = styled('div', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  border: '1px solid #DADADA',
  borderRadius: 8,
  background: '#fff',
  height: 38,
  paddingLeft: 12,
  variants: {
    readOnly: {
      true: { background: '#F5F5F5' },
      false: {},
    },
  },
});

export const RateInputDollar = styled('span', {
  fontSize: 14,
  color: '#5B5B5B',
  fontFamily: "'Manrope', sans-serif",
  marginRight: 4,
});

export const RateInput = styled('input', {
  flex: 1,
  height: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: 14,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
  padding: '0 12px 0 0',
  '&:read-only': {
    cursor: 'default',
    color: '#5B5B5B',
  },
});

export const PanelHeaderIcon = styled('img', {
  width: 28,
  height: 28,
  objectFit: 'contain',
  flexShrink: 0,
  filter:
    'brightness(0) saturate(100%) invert(46%) sepia(96%) saturate(362%) hue-rotate(84deg) brightness(96%) contrast(89%)',
});

export const PanelHeaderTitle = styled('p', {
  fontSize: 14,
  fontWeight: 700,
  color: '#1C1C1C',
  margin: 0,
  fontFamily: "'Manrope', sans-serif",
});

export const PanelEmpty = styled('div', {
  padding: '32px 20px',
  textAlign: 'center',
});

export const PanelEmptyText = styled('p', {
  fontSize: 13,
  color: '#979797',
  fontFamily: "'Manrope', sans-serif",
  margin: 0,
});

export const PanelColHeaders = styled('div', {
  display: 'grid',
  gridTemplateColumns: '96px 1fr 52px 40px',
  padding: '8px 16px',
  background: '#F9F9F9',
  borderBottom: '1px solid #EAEAEA',
});

export const PanelColLabel = styled('span', {
  fontSize: 11,
  fontWeight: 700,
  color: '#5B5B5B',
  fontFamily: "'Manrope', sans-serif",
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
});

export const PanelRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: '96px 1fr 52px 40px',
  padding: '10px 16px',
  alignItems: 'center',
  variants: {
    last: {
      true: {},
      false: { borderBottom: '1px solid #F0F0F0' },
    },
  },
});

export const PanelDateCell = styled('div', {
  fontSize: 13,
  fontWeight: 600,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
  lineHeight: 1.4,
});

export const PanelTimeCell = styled('div', {
  fontSize: 13,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
  lineHeight: 1.5,
});

export const PanelTimeMeta = styled('span', {
  color: '#5B5B5B',
  fontSize: 12,
});

export const PanelWorkersCell = styled('div', {
  fontSize: 13,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
});

export const PanelActionsCell = styled('div', {
  display: 'flex',
  gap: 6,
  alignItems: 'center',
});

export const PanelIconBtn = styled('button', {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 2,
  display: 'flex',
  alignItems: 'center',
});

// ── Popup ─────────────────────────────────────────────────────────────────

export const PopupOverlay = styled('div', {
  position: 'fixed',
  inset: 0,
  zIndex: 1999,
  variants: {
    dim: {
      true: { background: 'rgba(0,0,0,0.7)' },
      false: { background: 'transparent' },
    },
  },
});

export const Popup = styled('div', {
  position: 'fixed',
  zIndex: 2000,
  width: 400,
  background: '#FEFEFE',
  border: '1px solid #DADADA',
  borderRadius: 8,
  boxShadow: '0px 2px 6px rgba(0,0,0,0.12)',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: "'Manrope', sans-serif",
  overflow: 'visible',
});

export const PopupHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: '#E7F4E6',
  padding: '16px 20px 20px',
  borderRadius: '8px 8px 0 0',
});

export const PopupHeaderTitle = styled('span', {
  fontSize: 18,
  fontWeight: 700,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
});

export const PopupCloseBtn = styled('button', {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  width: 20,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const PopupBody = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  padding: 20,
});

export const PopupFields = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

export const PopupRow = styled('div', {
  display: 'flex',
  gap: 20,
  minHeight: 40,
  alignItems: 'center',
  width: '100%',
});

export const PopupRowLabel = styled('div', {
  width: 80,
  fontWeight: 700,
  fontSize: 16,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
  flexShrink: 0,
});

export const PopupDateValue = styled('span', {
  fontSize: 16,
  fontWeight: 400,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
});

export const TzLabel = styled('span', {
  fontSize: 16,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
  flexShrink: 0,
});

export const PopupSelectWrap = styled('div', {
  position: 'relative',
  flex: 1,
  minWidth: 0,
});

export const PopupSelect = styled('div', {
  border: '1px solid #DADADA',
  borderRadius: 8,
  padding: '4px 4px 4px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  background: '#fff',
  height: 34,
  boxSizing: 'border-box',
  variants: {
    disabled: {
      true: {
        background: '#F5F5F5',
        cursor: 'not-allowed',
        color: '#979797',
      },
      false: {},
    },
  },
});

export const PopupSelectValue = styled('span', {
  fontSize: 14,
  fontFamily: "'Manrope', sans-serif",
  whiteSpace: 'nowrap',
  variants: {
    placeholder: {
      true: { color: '#979797' },
      false: { color: '#1C1C1C' },
    },
  },
});

export const PopupDropdown = styled('div', {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  background: '#fff',
  border: '1px solid #DADADA',
  borderRadius: 8,
  zIndex: 3000,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
});

export const PopupDropdownScroll = styled('div', {
  maxHeight: 200,
  overflowY: 'auto',
});

export const PopupDropdownItem = styled('div', {
  padding: '10px 16px',
  cursor: 'pointer',
  fontSize: 14,
  fontFamily: "'Manrope', sans-serif",
  color: '#1C1C1C',
  '&:hover': { background: '#F5F5F5' },
});

export const PopupQtyInput = styled('input', {
  width: '100%',
  height: 34,
  border: '1px solid #DADADA',
  borderRadius: 8,
  padding: '4px 12px',
  fontSize: 14,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
  boxSizing: 'border-box',
  outline: 'none',
  background: '#fff',
});

export const PopupAddBtn = styled('button', {
  background: '#44A735',
  color: 'white',
  border: 'none',
  borderRadius: 8,
  height: 32,
  width: '100%',
  fontWeight: 700,
  fontSize: 12,
  cursor: 'pointer',
  boxShadow: '0px 2px 4px rgba(0,0,0,0.12)',
  fontFamily: "'Manrope', sans-serif",
  '&:hover': { background: '#2E8722' },
  '&:disabled': {
    background: '#C9C9C9',
    cursor: 'not-allowed',
    boxShadow: 'none',
    '&:hover': { background: '#C9C9C9' },
  },
});

export const PopupHelpText = styled('div', {
  fontSize: 12,
  color: '#5B5B5B',
  fontFamily: "'Manrope', sans-serif",
  lineHeight: 1.4,
  variants: {
    tone: {
      info: { color: '#5B5B5B' },
      error: { color: '#CF040C' },
    },
  },
});

export const PopupBreakRow = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  flex: 1,
  minWidth: 0,
});
